import { Component } from '@theme/component';

const RESET_MS = 150;

/**
 * Drives the autoplay progress ring (CSS pseudo-element) around the play/pause button of the
 * slideshow in the same section. Freezes on hover and eases back to zero on leave.
 */
class LnSlideshowAutoplayRing extends Component {
  /** @type {any} */
  #slideshow = null;
  /** @type {Element[]} */
  #buttons = [];
  /** @type {Animation[]} */
  #animations = [];
  #running = false;
  #progress = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  #pauseObserver = new MutationObserver(() => {
    if (!this.#slideshow?.paused) return;

    this.#running = false;
    clearInterval(this.#timer);
    this.#progress = 0;
    this.#animations.forEach((animation) => animation.cancel());
    this.#animations = [];
  });

  async connectedCallback() {
    super.connectedCallback();

    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;

    this.#slideshow = this.closest('.shopify-section')?.querySelector('slideshow-component[autoplay]');
    const slideshow = this.#slideshow;
    if (!slideshow || slideshow.dataset.lnRing) return;
    slideshow.dataset.lnRing = 'true';

    this.#buttons = [...slideshow.querySelectorAll('.icon-pause, .icon-play')];
    this.#pauseObserver.observe(slideshow, { attributes: true, attributeFilter: ['paused'] });

    const originalPlay = slideshow.play.bind(slideshow);
    const originalSuspend = slideshow.suspend.bind(slideshow);

    slideshow.play = (/** @type {number | Event} */ interval) => {
      const duration = typeof interval === 'number' ? interval : slideshow.autoplayInterval;
      originalPlay(duration);
      if (!slideshow.matches(':hover') && !document.hidden) {
        this.#start(duration);
      }
    };

    slideshow.suspend = () => {
      this.#freeze();
      originalSuspend();
    };

    // The native mouseenter listener keeps a reference to the unpatched suspend
    slideshow.addEventListener('mouseenter', this.#freeze);

    if (!slideshow.paused) slideshow.resume();
  }

  /** @param {number} duration */
  #start(duration) {
    if (this.#running) return;
    this.#running = true;

    this.#animate(this.#progress, duration);
    this.#progress = 0;
    this.#timer = setInterval(() => this.#animate(0, duration), duration);
  }

  /**
   * @param {number} from - Progress (0-1) the ring was frozen at.
   * @param {number} duration
   */
  #animate(from, duration) {
    this.#animations.forEach((animation) => animation.cancel());

    const frames =
      from > 0.001
        ? [
            { '--ln-ring-progress': `${from * 100}%`, offset: 0 },
            { '--ln-ring-progress': '0%', offset: Math.min(RESET_MS / duration, 0.5) },
            { '--ln-ring-progress': '100%', offset: 1 },
          ]
        : [{ '--ln-ring-progress': '0%' }, { '--ln-ring-progress': '100%' }];

    this.#animations = this.#buttons.map((button) =>
      button.animate(frames, { duration, easing: 'linear', fill: 'forwards', pseudoElement: '::after' })
    );
  }

  #freeze = () => {
    if (!this.#running) return;
    this.#running = false;
    clearInterval(this.#timer);

    const button = this.#buttons[0];
    this.#progress = button
      ? parseFloat(getComputedStyle(button, '::after').getPropertyValue('--ln-ring-progress')) / 100
      : 0;
    this.#animations.forEach((animation) => animation.pause());
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
    this.#animations.forEach((animation) => animation.cancel());
    this.#pauseObserver.disconnect();
    this.#slideshow?.removeEventListener('mouseenter', this.#freeze);
  }
}

if (!customElements.get('ln-slideshow-autoplay-ring')) {
  customElements.define('ln-slideshow-autoplay-ring', LnSlideshowAutoplayRing);
}
